import { useState } from "react";
import { BookOpen, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { submitPrintInterest } from "@/lib/printInterest";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ALL_SERIES = [
  { id: "cyber-safety", label: "LifeIQ Cyber Fraud & Scam Safety" },
  { id: "lifeiq-level-1", label: "LifeIQ Level 1" },
  { id: "lifeiq-level-2", label: "LifeIQ Level 2" },
  { id: "level3", label: "LifeIQ Level 3 · Life Design" },
];

const VISITOR_TYPES = [
  { value: "reader", label: "Reader" },
  { value: "parent_caregiver", label: "Parent / Caregiver" },
  { value: "teacher", label: "Teacher" },
  { value: "school_institution", label: "School / Institution" },
  { value: "other", label: "Other" },
];

const QUANTITY_BANDS = [
  { value: "1", label: "1 copy" },
  { value: "2-5", label: "2–5 copies" },
  { value: "6-20", label: "6–20 copies" },
  { value: "20+", label: "20+ / institutional interest" },
];

/**
 * Reusable Print Edition Interest trigger + modal (Command 02 §16–19).
 * This is an INTEREST REGISTRATION, not a preorder — no payment is ever
 * taken here. Single-book mode pre-selects one series/product silently;
 * multi mode (used from the master /books page) lets the visitor tick
 * one or more collections.
 */
export default function PrintInterestButton({
  mode = "single",
  seriesId,
  productId,
  bookTitle,
  sourcePage,
  label = "I'm Interested in Print →",
  className = "",
  testId = "print-interest-trigger",
  accentColor = "#F57F17",
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [contactMethod, setContactMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [city, setCity] = useState("");
  const [visitorType, setVisitorType] = useState("");
  const [quantityBand, setQuantityBand] = useState("");
  const [consent, setConsent] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState(mode === "single" && seriesId ? [seriesId] : []);

  const resetForm = () => {
    setStep("form");
    setName("");
    setContactMethod("email");
    setEmail("");
    setWhatsapp("");
    setCity("");
    setVisitorType("");
    setQuantityBand("");
    setConsent(false);
    setSelectedSeries(mode === "single" && seriesId ? [seriesId] : []);
    setError("");
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) {
      track("print_interest_open", { series_id: seriesId, product_id: productId, source_page: sourcePage });
    } else {
      setTimeout(resetForm, 200);
    }
  };

  const toggleSeries = (id) => {
    setSelectedSeries((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Please enter your name.");
    if (contactMethod === "email" && !email.trim()) return setError("Please enter your email, or switch to WhatsApp.");
    if (contactMethod === "whatsapp" && !whatsapp.trim()) return setError("Please enter your WhatsApp number, or switch to email.");
    if (selectedSeries.length === 0) return setError("Please select at least one book or collection.");
    if (!consent) return setError("Please confirm the consent checkbox below.");

    setSubmitting(true);
    track("print_interest_submit", { series_ids: selectedSeries, product_id: productId, source_page: sourcePage });
    try {
      await submitPrintInterest({
        name: name.trim(),
        email: contactMethod === "email" ? email.trim() : undefined,
        whatsapp: contactMethod === "whatsapp" ? whatsapp.trim() : undefined,
        city: city.trim() || undefined,
        visitor_type: visitorType || undefined,
        quantity_band: quantityBand || undefined,
        series_ids: selectedSeries,
        product_id: productId || undefined,
        book_title: bookTitle || undefined,
        source_page: sourcePage || undefined,
        consent: true,
      });
      track("print_interest_success", { series_ids: selectedSeries, product_id: productId, source_page: sourcePage });
      setStep("success");
    } catch (err) {
      setError(err.message || "Could not submit your interest — please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => onOpenChange(true)}
        className={className || "inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#5C6470] hover:text-[#0D1B2A] transition-colors"}
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="print-interest-modal" className="bg-[#FBF7EE] border-[#E5E1D3] max-w-md rounded-sm max-h-[85vh] overflow-y-auto">
          {step === "success" ? (
            <div data-testid="print-interest-success" className="py-4 text-center">
              <CheckCircle2 size={40} className="mx-auto" style={{ color: accentColor }} />
              <h3 className="mt-4 font-serif-d text-xl text-[#0D1B2A]">Thank you.</h3>
              <p className="mt-1 text-[15px] font-semibold text-[#0D1B2A]">Your print interest has been recorded.</p>
              <p className="mt-3 text-[13px] text-[#5C6470] leading-[1.6]">
                We'll let you know when this printed edition becomes available.
              </p>
              <button
                type="button"
                data-testid="print-interest-close-success"
                onClick={() => onOpenChange(false)}
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 text-[12px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: accentColor }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <DialogHeader>
                <DialogTitle className="text-[#0D1B2A] font-serif text-xl flex items-center gap-2">
                  <BookOpen size={18} style={{ color: accentColor }} /> Printed Edition Interest
                </DialogTitle>
                <DialogDescription className="text-[#5C6470] text-sm pt-1">
                  LifeIQ digital editions are available first. Printed editions are planned based partly on reader
                  demand. Tell us which books you'd like in print and we'll let you know when they become available.
                </DialogDescription>
              </DialogHeader>

              {mode === "multi" && (
                <div className="mt-2">
                  <Label className="text-[#0D1B2A] text-[13px] font-semibold">Which book(s) or collection(s)?</Label>
                  <div data-testid="print-interest-series-list" className="mt-2 space-y-2">
                    {ALL_SERIES.map((s) => (
                      <label key={s.id} className="flex items-center gap-2.5 text-[14px] text-[#0D1B2A] cursor-pointer">
                        <Checkbox
                          data-testid={`print-interest-series-${s.id}`}
                          checked={selectedSeries.includes(s.id)}
                          onCheckedChange={() => toggleSeries(s.id)}
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <Label htmlFor="pi-name" className="text-[#0D1B2A] text-[13px] font-semibold">Name</Label>
                <Input
                  id="pi-name"
                  data-testid="print-interest-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]"
                  autoFocus
                />
              </div>

              <div className="mt-4">
                <Label className="text-[#0D1B2A] text-[13px] font-semibold">Contact method</Label>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid="print-interest-contact-email-tab"
                    onClick={() => setContactMethod("email")}
                    className={`flex-1 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border transition-colors ${contactMethod === "email" ? "text-white" : "text-[#5C6470] border-[#D9D4C4]"}`}
                    style={contactMethod === "email" ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    data-testid="print-interest-contact-whatsapp-tab"
                    onClick={() => setContactMethod("whatsapp")}
                    className={`flex-1 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border transition-colors ${contactMethod === "whatsapp" ? "text-white" : "text-[#5C6470] border-[#D9D4C4]"}`}
                    style={contactMethod === "whatsapp" ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
                  >
                    WhatsApp
                  </button>
                </div>
                {contactMethod === "email" ? (
                  <Input
                    type="email"
                    data-testid="print-interest-email-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 border-[#D9D4C4] text-[#0D1B2A]"
                  />
                ) : (
                  <Input
                    type="tel"
                    data-testid="print-interest-whatsapp-input"
                    placeholder="+91 9XXXXXXXXX"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="mt-2 border-[#D9D4C4] text-[#0D1B2A]"
                  />
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pi-city" className="text-[#0D1B2A] text-[13px] font-semibold">City (optional)</Label>
                  <Input
                    id="pi-city"
                    data-testid="print-interest-city-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]"
                  />
                </div>
                <div>
                  <Label className="text-[#0D1B2A] text-[13px] font-semibold">I am a (optional)</Label>
                  <Select value={visitorType} onValueChange={setVisitorType}>
                    <SelectTrigger data-testid="print-interest-visitor-type-select" className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {VISITOR_TYPES.map((v) => (
                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-[#0D1B2A] text-[13px] font-semibold">Expected quantity (optional)</Label>
                <Select value={quantityBand} onValueChange={setQuantityBand}>
                  <SelectTrigger data-testid="print-interest-quantity-select" className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUANTITY_BANDS.map((q) => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label className="mt-4 flex items-start gap-2.5 text-[13px] text-[#5C6470] leading-[1.5] cursor-pointer">
                <Checkbox
                  data-testid="print-interest-consent-checkbox"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(Boolean(v))}
                  className="mt-0.5"
                />
                I'd like LifeIQ to notify me when the printed edition I selected becomes available.
              </label>
              <p className="mt-2 text-[12px] text-[#8A8578]">
                This is an interest registration, not a preorder. No payment is taken.
              </p>

              {error && (
                <p data-testid="print-interest-error" role="alert" className="mt-3 text-[13px] text-red-600">{error}</p>
              )}

              <button
                type="submit"
                data-testid="print-interest-submit-btn"
                disabled={submitting}
                style={{ backgroundColor: accentColor }}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 disabled:opacity-70 transition-opacity"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : null}
                {submitting ? "Submitting…" : "Register Print Interest"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
